import { useEffect, useRef } from 'react';
import { colors } from '@utils/constants/style';

/**
 * OAuth callback relay (#2302).
 *
 * The connect-wizard opens an OAuth popup pointed at the external
 * platform's authorize URL. The platform (Google / etc.) redirects
 * the popup back here with ``?code=…&state=…`` (success) or
 * ``?error=…`` (user denied / platform refused). This component is
 * the only thing the popup renders — it forwards the result to the
 * opener via ``postMessage`` and closes itself.
 *
 * Message shape matches what :func:`launchOAuthPopup` in
 * ``src/utils/oauth/popup.ts`` listens for.
 */
export default function OAuthCallback() {
  // Guard against double-dispatch under React StrictMode.
  const sent = useRef(false);

  useEffect(() => {
    if (sent.current) return;
    sent.current = true;

    const params = new URLSearchParams(window.location.search);
    const state = params.get('state') ?? '';
    const code = params.get('code') ?? '';
    const providerError = params.get('error') ?? '';
    const reason: 'denied' | 'cancelled' | undefined =
      providerError === 'access_denied'
        ? 'denied'
        : providerError
          ? 'cancelled'
          : undefined;

    const payload = {
      source: 'scriptix-oauth' as const,
      attempt_id: state,
      ok: !providerError && !!code,
      ...(code ? { code } : {}),
      ...(reason ? { reason } : {})
    };

    try {
      // Target the opener; the launchOAuthPopup helper's listener
      // filters by expected origin so a stray message leaking into
      // another tab is ignored.
      window.opener?.postMessage(payload, window.location.origin);
    } catch {
      // Opener may already be gone (user closed the primary tab).
      // Nothing to do; the popup still closes itself below.
    }

    // Give the message a tick to flush before the window dies.
    window.setTimeout(() => window.close(), 50);
  }, []);

  return (
    <main
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        fontSize: 14,
        color: colors.inactiveText.light
      }}
    >
      Returning you to Scriptix…
    </main>
  );
}
